import type { AxiosResponseT } from 'Interfaces/axiosResponse';
import { MobXProviderContext } from 'mobx-react';
import { useSharedItemAuthor } from 'Modules/sharedItems/hooks/useSharedItemAuthor/index';
import { ANALYTICS_EVENT_SHARED_FILES_LINKS } from 'Modules/sharedItems/index.constants';
import type { SharedItem } from 'Modules/sharedItems/index.types';
import {
  isSharedItemImage,
  mapSharedItemToMessageDocument,
} from 'Modules/sharedItems/utils';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { ensureUrlProtocol } from 'Utils/linkUtils';

export const useOpenSharedItem = () => {
  const {
    ui: { handleDownloadWithLink },
    message: { getFileDownloadLink },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const { setImagePreviewOpen } = useUIStore((state) => ({
    setImagePreviewOpen: state.setImagePreviewOpen,
  }));

  const [previewItem, setPreviewItem] = React.useState<SharedItem | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = React.useState(false);

  const { displayName: previewSenderName } = useSharedItemAuthor(
    previewItem?.author
  );

  // Fires the "shared_files_links_item_opened" analytics event for a shared item.
  const trackItemOpened = React.useCallback((item: SharedItem) => {
    pushToGTMDataLayer(ANALYTICS_EVENT_SHARED_FILES_LINKS.ITEM_OPENED, {
      conversation_id: item.conversationId,
      item_type: isSharedItemImage(item) ? 'image' : item.itemType,
    });
  }, []);

  /**
   * Downloads/opens a non-image file item. Falls back to opening targetUrl
   * directly if there's no fileInfoId or if the download link request fails.
   */
  const openFile = React.useCallback(
    async (item: SharedItem) => {
      const fileId = item.fileInfoId;
      if (!fileId) {
        if (item.targetUrl) {
          window.open(
            ensureUrlProtocol(item.targetUrl),
            '_blank',
            'noopener,noreferrer'
          );
        }
        return;
      }

      try {
        const file = (await getFileDownloadLink(fileId)) as AxiosResponseT<{
          url: string;
          fileName: string;
        }>;

        if (file?.data?.url) {
          handleDownloadWithLink(
            file.data.url,
            file.data.fileName || item.itemDisplayName
          );
        }
      } catch (error) {
        console.error('Error opening shared file:', error);
        if (item.targetUrl) {
          window.open(
            ensureUrlProtocol(item.targetUrl),
            '_blank',
            'noopener,noreferrer'
          );
        }
      }
    },
    [getFileDownloadLink, handleDownloadWithLink]
  );

  /**
   * Opens the in-app image preview modal for an image item, resolving the
   * fetched download URL (or falling back to targetUrl) as imageUrl.
   */
  const openImagePreview = React.useCallback(
    async (item: SharedItem) => {
      const previewId = item.fileInfoId || item.id;
      setPreviewItem(item);
      setImagePreviewOpen(previewId);
      setIsLoadingImage(true);
      setImageUrl(null);

      try {
        if (item.fileInfoId) {
          const file = (await getFileDownloadLink(
            item.fileInfoId
          )) as AxiosResponseT<{ url: string; fileName: string }>;

          if (file?.data?.url) {
            setImageUrl(file.data.url);
            return;
          }
        }

        if (item.targetUrl) {
          setImageUrl(item.targetUrl);
        }
      } catch (error) {
        console.error('Error fetching shared image URL:', error);
        if (item.targetUrl) {
          setImageUrl(item.targetUrl);
        }
      } finally {
        setIsLoadingImage(false);
      }
    },
    [getFileDownloadLink, setImagePreviewOpen]
  );

  /**
   * Entry point for opening a shared item: routes to image preview, external
   * link, or file download depending on item type. No-op for
   * unavailable/deleted items.
   */
  const handleOpenSharedItem = React.useCallback(
    async (item: SharedItem) => {
      if (item.isUnavailable || item.isDeleted) {
        return;
      }

      trackItemOpened(item);

      if (isSharedItemImage(item)) {
        await openImagePreview(item);
        return;
      }

      if (item.itemType === 'link') {
        if (item.targetUrl) {
          window.open(
            ensureUrlProtocol(item.targetUrl),
            '_blank',
            'noopener,noreferrer'
          );
        }
        return;
      }

      await openFile(item);
    },
    [openFile, openImagePreview, trackItemOpened]
  );

  // Clears preview state and closes the in-app image preview modal.
  const handleCloseImagePreview = React.useCallback(() => {
    setPreviewItem(null);
    setImageUrl(null);
    setIsLoadingImage(false);
    setImagePreviewOpen(null);
  }, [setImagePreviewOpen]);

  /**
   * Downloads the currently previewed image, given its externalId (from
   * previewDocument). Falls back to a generic "image" filename if the
   * item's display name is unavailable.
   */
  const handleDownloadPreviewImage = React.useCallback(
    async (externalId: string) => {
      try {
        const file = (await getFileDownloadLink(externalId)) as AxiosResponseT<{
          url: string;
          fileName: string;
        }>;

        if (file?.data?.url) {
          handleDownloadWithLink(
            file.data.url,
            file.data.fileName || previewItem?.itemDisplayName || 'image'
          );
        }
      } catch (error) {
        console.error('Error downloading shared image:', error);
      }
    },
    [getFileDownloadLink, handleDownloadWithLink, previewItem?.itemDisplayName]
  );

  const previewDocument = previewItem
    ? mapSharedItemToMessageDocument(previewItem)
    : null;

  return {
    handleOpenSharedItem,
    handleCloseImagePreview,
    handleDownloadPreviewImage,
    previewItem,
    previewDocument,
    previewSenderName: previewItem ? previewSenderName : '',
    imageUrl,
    isLoadingImage,
  };
};